import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { API_URL, CSRF_HEADER } from "constants/api";
import { getCsrfToken, setCsrfToken } from "utils/csrf-token";
import { isSuccessStatus } from "utils/is-success-status";
import { refreshScheduler } from "utils/refresh-scheduler";
import { versionManagement } from "utils/version-management";
import { getStoredLanguage } from "utils/get-language";

type RetriedRequestConfig = InternalAxiosRequestConfig & { __retried?: boolean };

type SessionCredentialsResponse = {
  csrfToken?: string;
  accessTokenExpiresIn?: number;
};

const AUTH_PATHS = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/verify-otp", "/auth/resend-otp"];

let sessionLostHandler: (() => void) | null = null;
let refreshPromise: Promise<boolean> | null = null;
let rotationDepth = 0;
let sessionLossReported = false;

export const setSessionLostHandler = (handler: (() => void) | null) => {
  sessionLostHandler = handler;

  if (handler) {
    sessionLossReported = false;
  }
};

const isAuthPath = (url?: string) => {
  if (!url) {
    return false;
  }

  return AUTH_PATHS.includes(url.split("?")[0]);
};

const reportSessionLost = () => {
  refreshScheduler.disarm();

  if (sessionLossReported) {
    return;
  }

  // Parallel unauthorized responses share one refresh failure and one user-visible transition.
  sessionLossReported = true;
  sessionLostHandler?.();
};

const enterRotation = () => {
  if (rotationDepth === 0) {
    versionManagement.deferReloads();
  }

  rotationDepth += 1;
};

const leaveRotation = () => {
  rotationDepth -= 1;

  if (rotationDepth === 0) {
    versionManagement.resumeReloads();
  }
};

const apiClient = axios.create({
  baseURL: `${API_URL}/api`,
  validateStatus: () => true,
  withCredentials: true,
});

const refreshSession = (): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = apiClient
      .post<SessionCredentialsResponse>("/auth/refresh")
      .then((response) => {
        if (!isSuccessStatus(response.status)) {
          return false;
        }

        if (response.data?.csrfToken) {
          setCsrfToken(response.data.csrfToken);
        }

        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const rotateSession = async (): Promise<boolean> => {
  enterRotation();

  try {
    return await refreshSession();
  } finally {
    leaveRotation();
  }
};

refreshScheduler.setRefreshHandler(rotateSession);

apiClient.interceptors.request.use(async (config) => {
  if (config.data instanceof FormData && !isAuthPath(config.url) && refreshScheduler.isExpiringSoon()) {
    await rotateSession();
  }

  config.headers["Accept-Language"] = getStoredLanguage();
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    config.headers[CSRF_HEADER] = csrfToken;
  }
  return config;
});

apiClient.interceptors.response.use((response) => {
  const version = response.headers?.["x-app-version"];

  if (version) {
    versionManagement.checkAndHandleVersion(version);
  }

  return response;
});

apiClient.interceptors.response.use(async (response): Promise<AxiosResponse> => {
  const data: unknown = response.data;

  if (isSuccessStatus(response.status) && typeof data === "object" && data !== null && "accessTokenExpiresIn" in data) {
    const expiresIn = data.accessTokenExpiresIn;

    if (typeof expiresIn === "number" && expiresIn > 0) {
      refreshScheduler.arm(expiresIn);
    }
  }

  if (response.status !== 401) {
    return response;
  }

  const config = response.config as RetriedRequestConfig;

  if (config.__retried || isAuthPath(config.url)) {
    return response;
  }

  config.__retried = true;
  enterRotation();

  try {
    const refreshed = await refreshSession();

    if (!refreshed) {
      reportSessionLost();

      return response;
    }

    return await apiClient.request(config);
  } catch {
    return response;
  } finally {
    leaveRotation();
  }
});

export default apiClient;
